import { useState, useRef, useEffect, useCallback, useId } from "react";
import { FormFieldOption } from "@aam/shared";

interface StateComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: FormFieldOption[];
  placeholder?: string;
  required?: boolean;
  id?: string;
  describedBy?: string;
  invalid?: boolean;
}

export function StateCombobox({ value, onChange, options, placeholder, required, id, describedBy, invalid }: StateComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const inputId = id ?? `${generatedId}-input`;
  const listboxId = `${generatedId}-listbox`;

  const selectedOption = options.find((o) => o.value === value);

  const filtered = query.trim()
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(query.toLowerCase()) ||
          o.value.toLowerCase().includes(query.toLowerCase())
      )
    : options;

  const handleSelect = useCallback(
    (option: FormFieldOption) => {
      onChange(option.value);
      setQuery("");
      setOpen(false);
      setActiveIdx(-1);
    },
    [onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        setOpen(true);
        setActiveIdx(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIdx((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter" && activeIdx >= 0) {
      handleSelect(filtered[activeIdx]);
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      setActiveIdx(-1);
    } else if (e.key === "Home") {
      setActiveIdx(0);
      e.preventDefault();
    } else if (e.key === "End") {
      setActiveIdx(filtered.length - 1);
      e.preventDefault();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      const item = listRef.current.children[activeIdx] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
        setActiveIdx(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div
        className="relative"
        onClick={() => {
          setOpen(true);
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
      >
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            value={open ? query : selectedOption ? `${selectedOption.value} — ${selectedOption.label}` : ""}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setOpen(true)}
            placeholder={open ? "Type to search…" : (placeholder ?? "Select a state…")}
            readOnly={!open}
            required={required && !value}
            className="w-full px-4 py-2.5 rounded-xl bg-surface-input border border-border-default text-content-primary placeholder:text-content-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-sm pr-10"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={open && activeIdx >= 0 ? `${generatedId}-option-${activeIdx}` : undefined}
            aria-describedby={describedBy}
            aria-invalid={invalid}
          />

        {open && (
          <button
            type="button"
            className="absolute right-1 top-1/2 min-h-10 min-w-10 -translate-y-1/2 text-content-muted hover:text-content-primary"
            aria-label="Close state options"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              setQuery("");
            }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          className="absolute z-50 mt-1.5 w-full max-h-60 overflow-y-auto rounded-xl bg-surface-raised border border-border-default shadow-card-hover py-1 animate-fade-in"
          role="listbox"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-3 text-sm text-content-muted text-center" role="presentation">No states found</li>
          ) : (
            filtered.map((option, idx) => (
              <li
                key={option.value}
                id={`${generatedId}-option-${idx}`}
                role="option"
                aria-selected={option.value === value}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                  idx === activeIdx
                    ? "bg-accent-strong text-on-accent"
                    : option.value === value
                    ? "bg-accent-muted text-content-primary"
                    : "text-content-secondary hover:bg-surface-overlay"
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(option);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
              >
                <span className="w-8 shrink-0 font-mono text-xs font-semibold opacity-60">{option.value}</span>
                <span>{option.label}</span>
                {option.value === value && (
                  <svg className="ml-auto w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
